import { ViewportScroller } from "@angular/common";
import { Component, inject, OnDestroy, OnInit, Renderer2, ChangeDetectionStrategy } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Event, NavigationEnd, Router, RouterEvent, RouterOutlet, Scroll } from "@angular/router";
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { ScrollService } from "common/routing/scroll.service";
import { MainService } from "core/main.service";
import { of, Subject } from "rxjs";
import { filter, map, mergeMap, takeUntil } from "rxjs/operators";

@Component({
	selector: "stages-app",
	templateUrl: "./app.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [RouterOutlet],
})
export class AppComponent implements OnInit, OnDestroy {
	private renderer = inject(Renderer2);
	private mainService = inject(MainService);
	private translateService = inject(TranslateService);
	private router = inject(Router);
	private activatedRoute = inject(ActivatedRoute);
	private titleService = inject(Title);
	private viewportScroller = inject(ViewportScroller);
	private scrollService = inject(ScrollService);

	private destroy$ = new Subject<boolean>();

	ngOnInit(): void {
		this.setUpPageTitleUpdate();

		this.router.events
			.pipe(filter((e: Event | RouterEvent): e is Scroll => e instanceof Scroll))
			.pipe(takeUntil(this.destroy$))
			.subscribe((e) => {
				if (e.position) {
					// backward navigation
					this.viewportScroller.scrollToPosition(e.position);
				} else if (e.anchor) {
					// anchor navigation
					this.viewportScroller.scrollToAnchor(e.anchor);
				} else {
					// forward navigation
					if (this.scrollService.scrollPosition === "top") {
						this.viewportScroller.scrollToPosition([0, 0]);
					}
				}
				this.scrollService.scrollPosition = "top";
			});

		this.mainService.applicationState.scrollDisabledChange.pipe(takeUntil(this.destroy$)).subscribe((disabled) => {
			if (disabled) {
				this.renderer.addClass(document.documentElement, "disable-scroll");
				this.renderer.addClass(document.body, "disable-scroll");
			} else {
				this.renderer.removeClass(document.documentElement, "disable-scroll");
				this.renderer.removeClass(document.body, "disable-scroll");
			}
		});

		if (this.translateService.currentLang) {
			this.renderer.setAttribute(
				document.getElementById("html-index-element"),
				"lang",
				this.translateService.currentLang,
			);
			this.renderer.setAttribute(document.body, "lang", this.translateService.currentLang);
		}
		this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
			this.renderer.setAttribute(
				document.getElementById("html-index-element"),
				"lang",
				this.translateService.currentLang,
			);
			this.renderer.setAttribute(document.body, "lang", event.lang);
		});
	}

	setUpPageTitleUpdate(): void {
		this.router.events
			.pipe(
				// See https://toddmotto.com/dynamic-page-titles-angular-2-router-events
				filter((event) => event instanceof NavigationEnd),
				map(() => this.activatedRoute),
				map((route) => {
					let childRoute = route;
					while (childRoute.firstChild) {
						childRoute = childRoute.firstChild;
					}
					return childRoute;
				}),
				filter((route) => route.outlet === "primary"),
				mergeMap((route) => route.data),
				mergeMap((data) => {
					if (data.title) {
						return of(data.title);
					}

					if (data.titleKey) {
						return this.translateService.get(data.titleKey);
					}

					return of("Stages");
				}),
				takeUntil(this.destroy$),
			)
			.subscribe((title) => {
				this.titleService.setTitle(title);
			});
	}

	ngOnDestroy(): void {
		this.destroy$.next(true);
		this.destroy$.complete();
	}
}
